/**
 * The pure statements MapSection's controller reads its numbers from: camera
 * frames, angle helpers, the scroll and closing arithmetic, the geometry of the
 * cities the camera may frame, and the shape check for the vendored map module
 * it loads by path. They live here rather than in the controller because that
 * file is at its authored-file size budget, and because none of them needs the
 * map, React or the DOM.
 */

import type * as MaplibreGl from "maplibre-gl";

import { toAsciiUpper } from "@noko-city/shared";

import type { LandingCity } from "@/lib/api";

/** One camera frame: the only thing the controller ever writes to the map. */
export type Camera = { center: [number, number]; zoom: number; bearing: number; pitch: number };

/** Which chapter of the scroll timeline a progress value sits in. */
export type Chapter = "orbit" | "approach" | "cities" | "closing";

/**
 * Scroll-timeline keyframes. The orbit zoom keeps the redesign's +2.15 shift
 * from the contract's 0.45, because this vendored build draws the planet far
 * smaller than the design assumed — a pixel census of the rendered canvas puts
 * zoom 0.45 at a 214px sphere on a 1440x1000 frame (~21svh), where the target is
 * 75-95svh — landing the orbit chapter at a ~794px (79svh) sphere. Approach and
 * cities were retuned so a scroll visibly turns the planet and lands the last
 * chapter on the target city's own region.
 */
export const ORBIT_ZOOM = 2.6;
const APPROACH_ZOOM = 3.75;
const CITIES_ZOOM = 5.55;

export const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);
const mix = (from: number, to: number, t: number): number => from + (to - from) * t;
/** Longitude is an angle on a circle: always interpolate the short way round. */
const mixLng = (from: number, to: number, t: number): number =>
  from + (((((to - from) % 360) + 540) % 360) - 180) * t;

/**
 * Hermite easing (t*t*(3-2*t)), shared by the closing pullback and the finale's
 * spin blend so both leave and arrive with zero slope: the camera must not kick
 * when the closing chapter begins, and the Earth must not jump into step with
 * the lunar orbit at the same instant.
 */
export const smoothstep = (t: number): number => t * t * (3 - 2 * t);

export const mixCamera = (from: Camera, to: Camera, t: number): Camera => ({
  center: [mixLng(from.center[0], to.center[0], t), mix(from.center[1], to.center[1], t)],
  zoom: mix(from.zoom, to.zoom, t),
  bearing: mix(from.bearing, to.bearing, t),
  pitch: mix(from.pitch, to.pitch, t),
});

/**
 * The destination frame: the target city itself at regional zoom, squared back
 * to north. The timeline's last keyframe and the CityIndex preview flight both
 * land on this one frame, so they cannot drift apart.
 */
export const cityCameraAt = (target: [number, number]): Camera => ({
  center: [target[1], target[0]],
  zoom: CITIES_ZOOM,
  bearing: 0,
  pitch: 0,
});

/**
 * Longitude is a circle, so the opening offset can push a cluster east of the
 * antimeridian: wrap it back onto [-180, 180) before the mix sees it.
 */
const wrapLng = (lng: number): number => ((((lng + 180) % 360) + 360) % 360) - 180;

/**
 * The head start the opening frame gives the cluster: opening 18° west of the
 * cities' own longitude parks them on the right-hand face of the globe, so the
 * idle drift rotates them into centre instead of leaving them wherever a fixed
 * meridian put them.
 */
export const OPENING_LNG_OFFSET = 18;

/**
 * The narrower head start the phone stage needs. That stage is a third of the
 * desktop's width, so at the desktop offset the far city of a two-city payload
 * starts past the right edge — measured, the İzmir/Tbilisi pair leaves its second
 * marker at x=326 on a 320px stage, entirely offscreen. Six degrees less west is
 * what hands `cameraAt` a cluster far enough east to bring that marker back.
 */
export const OPENING_LNG_OFFSET_PHONE = 12;

/**
 * The camera for a scroll progress: p=0 opens on the displayed cluster's own
 * longitude less the opening offset — so the cluster starts east of centre
 * rather than on the old fixed meridian — with the bearing well off north; p≈.33
 * swings it 10° west of the timeline target; p=1 lands on that city itself at
 * regional zoom. Both opening coordinates are clamped: latitude to ±60° so a
 * polar cluster still frames the sphere, longitude by the wrap above. Only the
 * geographic focus moved — the -42° opening bearing and the responsive padding
 * that parks the globe over the hero copy are the frames they always were. One
 * writer interpolates all three, and the wide bearing sweep is what makes the
 * scroll visibly turn the planet.
 */
export const cameraAt = (
  p: number,
  target: [number, number],
  openingTarget: [number, number],
): Camera => {
  const orbit: Camera = {
    center: [wrapLng(openingTarget[1] - OPENING_LNG_OFFSET), clamp(openingTarget[0], -60, 60)],
    zoom: ORBIT_ZOOM,
    bearing: -42,
    pitch: 0,
  };
  const approach: Camera = {
    center: [target[1] - 10, clamp(target[0], -60, 60)],
    zoom: APPROACH_ZOOM,
    bearing: -10,
    pitch: 0,
  };
  const city: Camera = cityCameraAt(target);
  return p <= 0.5
    ? mixCamera(orbit, approach, p / 0.5)
    : mixCamera(approach, city, (p - 0.5) / 0.5);
};

/**
 * Weight of the idle drift at a timeline progress: fully on through the orbit
 * chapter and faded to exactly zero by the end of it (q = 3p), so the drift is
 * continuous at the current scroll position and a page scrolled past the orbit
 * never carries drift into the approach.
 */
export const driftWeightAt = (p: number): number => {
  const q = clamp(3 * p, 0, 1);
  return 1 - (3 * q * q - 2 * q * q * q);
};

/**
 * How much faster the idle drift turns the planet while no city is on its facing
 * side, and how fast that multiplier itself may change.
 *
 * Measured on both builds side by side: the drift runs east at exactly 0.500
 * deg/s, and the live payload's cities leave the facing face 95 to 98 degrees of
 * longitude past the camera's aim (the spread is their latitude) — 135 degrees, 4.5
 * minutes, before the first one came round again, and the catch-up brought it back
 * 11.8 seconds after the last left. An equatorial fixture of the same span measured
 * the worst case, 189 degrees and 6.3 minutes, first city back after 16.2 seconds.
 * 24x is what does it: 12 deg/s, a revolution every 30s rather than most of a turn
 * of empty ocean per coffee break.
 */
export const SPIN_CATCH_UP_MAX = 24;
/**
 * Multiplier units per second: 24x is reached in 1.15s and released over the same
 * span, so the planet spins up and settles without a kick at either end — the
 * multiplier is continuous, which makes the angular rate continuous.
 */
export const SPIN_CATCH_UP_RAMP = 20;

/** The catch-up multiplier one frame further on, rate-limited to `SPIN_CATCH_UP_RAMP`. */
export const rampCatchUp = (current: number, catchingUp: boolean, seconds: number): number =>
  clamp(current + (catchingUp ? 1 : -1) * SPIN_CATCH_UP_RAMP * seconds, 1, SPIN_CATCH_UP_MAX);

export const chapterAt = (p: number): Chapter =>
  p < 1 / 3 ? "orbit" : p < 2 / 3 ? "approach" : "cities";

/**
 * Opacity of the opening's decorative planes — the three foreground rings — at a
 * scroll position. Scroll-linked rather than chapter-linked: the chapter only
 * leaves "orbit" once a third of the scene has passed, so a chapter-driven hide
 * left the rings crossing a hero that had already been partly scrolled away.
 * This fade starts the moment the hero begins to leave, is finished within the
 * first 15% of a viewport height, and reverses exactly with the scroll, so
 * returning to the top brings the rings back. The 0.15 threshold is a chosen
 * tuning value, not a measurement.
 */
export const openingDecorationOpacity = (
  scrollY: number,
  sceneTop: number,
  height: number,
): number => (height > 0 ? 1 - smoothstep(clamp((scrollY - sceneTop) / (0.15 * height), 0, 1)) : 0);

/**
 * Timeline progress through the scene: 0 at the scene top, 1 at the cities
 * beat. A collapsed scene — or a missing cities beat, which the controller
 * caches as the scene top — reads as "directory reached", so the camera never
 * parks short of the directory it just scrolled past.
 */
export const sceneProgressAt = (scrollY: number, sceneTop: number, citiesTop: number): number => {
  const range = citiesTop - sceneTop;
  if (!(range > 0)) return 1;
  return clamp((scrollY - sceneTop) / range, 0, 1);
};

/**
 * Progress through the closing finale: 0 until the closing section's top
 * reaches the viewport top (its entry), 1 at the document's maximum scroll.
 * `scrollY - closingTop` would be wrong here — the closing section is no taller
 * than the viewport, so its top never scrolls past the viewport top and that
 * measure would never advance. A missing closing beat arrives as an infinite
 * top, which fails the positive-range test below and so reads 0 unaided.
 */
export const closingProgressAt = (
  scrollY: number,
  closingTop: number,
  innerHeight: number,
  docEnd: number,
): number => {
  const entry = closingTop - innerHeight;
  const range = docEnd - entry;
  if (!(range > 0)) return 0;
  return clamp((scrollY - entry) / range, 0, 1);
};

/** Map padding plus the Earth diameter the closing finale pulls back to. */
export type Framing = {
  top: number;
  right: number;
  bottom: number;
  left: number;
  diameter: number;
};

/**
 * The one breakpoint table for both jobs: the map padding that parks the sphere
 * in the empty half of the stage, and the diameter the finale zooms the Earth
 * out to. They have to come from one table — a padding box that disagreed with
 * the closing diameter would leave the padding and the zoom fighting over where
 * the sphere sits. `width >= 1024` is the desktop band, `768…1023` the tablet
 * band that also narrows the left padding, and below that the sphere moves to
 * the top of the stage with the text below it.
 */
export const closingFraming = (width: number, height: number): Framing => {
  const wide = width >= 1024;
  const tablet = width >= 768;
  const diameter = wide
    ? Math.min(0.38 * width, 0.52 * height)
    : tablet
      ? Math.min(0.38 * width, 0.44 * height)
      : Math.min(0.66 * width, 0.3 * height);
  return {
    top: 0,
    right: 0,
    bottom: tablet ? 0 : height * 0.55,
    left: wide ? width * 0.5 : tablet ? width * 0.45 : 0,
    diameter,
  };
};

/** Radius px per px of stage height, and the measured base radius. */
const OPENING_EARTH_SLOPE = 0.086;
const OPENING_EARTH_BASE = 305;

/**
 * The Earth's opening-frame diameter as a browser measurement rather than a
 * rule of thumb: diffing the page against the same page with `.atlas-map`
 * hidden — so only the real globe is left — at reduced motion, pinned on the
 * opening frame (zoom 2.6), across nine viewports, puts the rendered disc at
 * 2 * (0.086 * stageHeight + 305) px. The disc is affine in the stage height
 * and independent of the stage width (900x1200 and 1800x1200 both measured
 * r=408; 390x844 r=372). Six readings land within 0.7% of the law and the two
 * phone ones within 1.9%, which is the page diff's own resolution, not a
 * different geometry. The
 * earlier "794px sphere on a 1000px stage" figure was one reading of the same
 * law: openingEarthDiameter(1000) = 782. MapLibre projects the globe at a
 * radius linear in worldSize, so scaling the disc by 2^zoom is exact, and this
 * one measurement is what the closing zoom is calibrated against.
 */
export const openingEarthDiameter = (stageHeight: number): number =>
  2 * (OPENING_EARTH_SLOPE * stageHeight + OPENING_EARTH_BASE);

/**
 * Correction for the closing zoom, applied after the analytical size ratio.
 * Measured in the browser, not derived: the vendored globe does NOT scale its
 * disc as 2^zoom — a page diff against the map-hidden frame gives 380px of disc
 * at zoom 2.6 against 297px at 1.841, and the response gets closer to 1:1 again
 * as the finale zooms out. This is the second measured pass: the first (-0.8)
 * was fitted from the opening-to-closing response and overshot, landing the
 * finale 11-20% under the target at all six review viewports. Iterating against
 * those measurements lands each of them within ~7% of the target diameter.
 * Constants stay here, never a live pixel-reading loop in the frame path.
 */
export const CLOSING_ZOOM_CALIBRATION = -0.5;

/** The map's own floor, matching the `minZoom` its construction passes. */
const MIN_ZOOM = -2;

/**
 * The zoom that draws the Earth `diameter` px across on a stage of this height:
 * doubling a drawn diameter is one zoom level, so the correction over the
 * opening frame is the log2 of the size ratio, clamped to the map's range.
 */
export const closingZoomFor = (diameter: number, stageHeight: number): number => {
  const levels =
    ORBIT_ZOOM + Math.log2(diameter / openingEarthDiameter(stageHeight)) + CLOSING_ZOOM_CALIBRATION;
  return clamp(levels, MIN_ZOOM, ORBIT_ZOOM);
};

/**
 * The closing frame for a closing progress: c=0 is exactly the frame the finale
 * was entered from — the original timeline read at the entry scroll position,
 * so the pullback leaves from wherever the camera already was and never snaps
 * to another hemisphere — and c=1 is the target city centred under the wide
 * closing zoom, squared back up to north. Eased so the pullback neither starts
 * nor stops with a kick; `mixCamera` wraps the longitude the short way.
 */
export const closingCameraAt = (
  c: number,
  from: Camera,
  target: [number, number],
  zoom: number,
): Camera => {
  const t = clamp(c, 0, 1);
  const eased = smoothstep(t);
  return mixCamera(from, { ...cityCameraAt(target), zoom }, eased);
};

/**
 * The closing chapter's destination frame: the timeline frame the finale was
 * entered from, pulled back to the selected city under the wide closing zoom,
 * with the angular drift already composed in. The drift rides
 * `smoothstep(closing)` so entering the finale adds no kick and the settled
 * finale turns at exactly the lunar rate.
 */
export const closingDriftFrame = (
  closing: number,
  from: Camera,
  target: [number, number],
  diameter: number,
  stageHeight: number,
  drift: number,
): Camera => {
  const camera = closingCameraAt(closing, from, target, closingZoomFor(diameter, stageHeight));
  camera.center[0] += drift * smoothstep(closing);
  return camera;
};

/**
 * The scroll timeline's destination frame, with the drift composed at its
 * chapter's weight. The drift belongs to the frame itself and is never added
 * after an interpolation: the two frames an ease mixes between both already
 * contain it, so adding it again at the end would double the angle at t≈0.
 */
export const timelineDriftFrame = (
  p: number,
  target: [number, number],
  openingTarget: [number, number],
  drift: number,
): Camera => {
  const camera = cameraAt(p, target, openingTarget);
  camera.center[0] += drift * driftWeightAt(p);
  return camera;
};

/**
 * Opacity of the two spacecraft. They leave with the opening's decorative planes
 * and stay gone through the approach, the directory and the start of the finale,
 * then return quietly once the pullback is established — 0.65 at the settled
 * frame — so they never reappear over the opening rings they just left.
 */
export const finaleSatelliteOpacity = (openingOpacity: number, closing: number): number =>
  closing > 0 ? 0.65 * smoothstep(clamp((closing - 0.55) / 0.25, 0, 1)) : openingOpacity;

/*
 * The finale's non-map art, the same pure statements for the same reason: where
 * the padded globe actually sits on the stage, where the moon rides its
 * stylized upper orbit, and where the two satellites ride — their opening rings
 * first, then the compact ellipse around the smaller finale Earth. The
 * controller writes these boxes and reads nothing back.
 */

/** A rendered box in absolute stage pixels: what the controller writes inline. */
export type Placement = { x: number; y: number; width: number; height: number };

/**
 * The centre of the finale's padded box, in stage pixels. It is the same centre
 * `--sphere-x`/`--sphere-y` derive from `closingFraming`, so the rings, the
 * satellites and the globe the map draws share one origin instead of three
 * near-misses that drift apart whenever the padding table changes.
 */
export const globeCentre = (width: number, height: number): { cx: number; cy: number } => {
  const { top, right, bottom, left } = closingFraming(width, height);
  return { cx: (width + left - right) / 2, cy: (height + top - bottom) / 2 };
};

/**
 * The moon's own ellipse: a shallow arc over the globe's upper hemisphere that
 * keeps the whole disc in frame at every angle, which a true circular orbit
 * cannot do — it either crosses the Earth's face or leaves the stage. The disc
 * is now a small real globe — the same MapLibre sphere the Earth uses, carrying
 * lunar maria — where the review rejected the oversized flat disc this used to
 * draw: 0.12 of the finale's Earth diameter reads ≈53.5px at 1568x857 and
 * ≈30.4px at 390x844, and the trajectory below only says where that globe rides.
 * The 32px horizontal reserve is still the room its marker label needs to stay
 * readable, the moonRadius+12 floor holds the disc clear of the edges, and the
 * 0.30*earthRadius ceiling holds the arc above the equator. Angle 0 is the right
 * shoulder, so the moon enters from the side it came in on. Deliberately
 * stylized framing, not a simulated orbit.
 */
export const moonOrbitAt = (
  width: number,
  height: number,
  earthDiameter: number,
  angleDegrees: number,
): { x: number; y: number; diameter: number } => {
  const { cx, cy } = globeCentre(width, height);
  const earthRadius = earthDiameter / 2;
  const diameter = clamp(0.12 * earthDiameter, 28, 56);
  const moonRadius = diameter / 2;
  const rx = Math.max(
    0,
    Math.min(1.05 * earthRadius, cx - moonRadius - 32, width - cx - moonRadius - 32),
  );
  const orbitY = Math.max(moonRadius + 12, cy - 0.85 * earthRadius);
  const ry = Math.max(
    0,
    Math.min(0.3 * earthRadius, orbitY - moonRadius - 12, cy - moonRadius - orbitY),
  );
  const angle = (angleDegrees * Math.PI) / 180;
  return { x: cx + rx * Math.cos(angle), y: orbitY - ry * Math.sin(angle), diameter };
};

/**
 * A point on a ring: `a`/`b` are the half-axes of that ring's own border box and
 * the rotation below is the one the ring's CSS `rotate()` applies, so a satellite
 * rides the exact ellipse the page draws instead of a circle that merely
 * approximates it. The result is an offset from `globeCentre`, which is the
 * origin both the rings and the globe are placed from.
 */
export const satellitePointAt = (
  a: number,
  b: number,
  inclinationDegrees: number,
  angleDegrees: number,
): { x: number; y: number } => {
  const t = (angleDegrees * Math.PI) / 180;
  const i = (inclinationDegrees * Math.PI) / 180;
  const u = a * Math.cos(t);
  const v = b * Math.sin(t);
  return { x: u * Math.cos(i) - v * Math.sin(i), y: u * Math.sin(i) + v * Math.cos(i) };
};

/** Each satellite's inclination: the rotation that ring's CSS applies to it. */
const ISS_INCLINATION = -14;
const SPUTNIK_INCLINATION = -42;

/**
 * Sputnik's phase offset: both spacecraft share the one clock, but not one start
 * line, so they cross the frame apart instead of travelling together. It is a
 * constant added to a phase that is already a pure function of the shared
 * `spinDegrees`, so scroll — either way — and reduced motion behave exactly as
 * they did before, and no second clock, timer or state is introduced.
 *
 * 250 is the offset that maximises the worst-case separation, measured rather
 * than reasoned: sweeping 0..345 in 15s put the peak at 255 (worst 115.1px) and a
 * finer pass 245..256 landed on 250 (worst 115.2px) inside a flat 0.2%-tall
 * maximum; the tempting half-orbit 180 manages only 94.1px (81.7% of the best),
 * because the two inclinations make the arcs closest approach tighter than a
 * half-orbit offset suggests. Worst case is the minimum centre-to-centre distance
 * over a full orbit at 1568x857, 768x1024, 390x844 and 320x740, and it rises from
 * 23.8px at the previous lockstep 0 to 115.2px here.
 */
const SPUTNIK_PHASE_OFFSET = 250;

/**
 * The finale's own ellipse around the small Earth: nominal half-axes as
 * fractions of the final Earth diameter, and the fraction of its breakpoint
 * artwork each spacecraft keeps once it has closed in. The opening half-axes are
 * the measured ring boxes instead — the rings are the opening system, and by the
 * finale they are gone.
 */
const FINALE_AXIS_X = 0.68;
const FINALE_AXIS_Y = 0.24;
const FINALE_ART_SCALE = 0.75;

/** Clearance a bounded orbit keeps between itself and the stage edge, in px. */
const ORBIT_EDGE_CLEARANCE = 12;

/**
 * Each spacecraft's opening artwork box, wide first and phone second. They are
 * the sizes the rings were drawn for; the finale scales from the wide ones, which
 * is why they are named rather than inlined at the call site.
 */
const ISS_ART: [number, number] = [72, 43.2];
const ISS_ART_PHONE: [number, number] = [54, 32.4];
const SPUTNIK_ART: [number, number] = [42, 42];
const SPUTNIK_ART_PHONE: [number, number] = [34, 34];

/**
 * One spacecraft's box for one frame. `opening` is the half-axis pair of the
 * ring it rides in the opening chapters and `angleDegrees` is read on the
 * ellipse `satellitePointAt` draws through those axes.
 *
 * The finale axes come from the final Earth's diameter, not from the rings: by
 * then the rings are gone and the spacecraft circle the smaller Earth instead.
 * They are then shrunk uniformly until the whole rotated ellipse and the artwork
 * riding it fit inside the stage. The envelope of a rotated ellipse is the half
 * extents of its bounding box, so the room left over after the artwork's own
 * half size and a small clearance, over that envelope, is the factor that bounds
 * both the complete track and its art in frame — a bounding box derivation, not
 * a measurement, and deliberately not a per-frame collision test. The factor is
 * applied to the finale axes only, then interpolated from the opening ones.
 */
const satelliteBox = (
  centre: { cx: number; cy: number },
  stage: [number, number],
  ease: number,
  angleDegrees: number,
  opening: [number, number],
  inclinationDegrees: number,
  earthDiameter: number,
  art: [number, number],
): Placement => {
  const i = (inclinationDegrees * Math.PI) / 180;
  const finalA = FINALE_AXIS_X * earthDiameter;
  const finalB = FINALE_AXIS_Y * earthDiameter;
  const halfW = (art[0] * FINALE_ART_SCALE) / 2;
  const halfH = (art[1] * FINALE_ART_SCALE) / 2;
  const bound = clamp(
    Math.min(
      1,
      (Math.min(centre.cx, stage[0] - centre.cx) - halfW - ORBIT_EDGE_CLEARANCE) /
        Math.hypot(finalA * Math.cos(i), finalB * Math.sin(i)),
      (Math.min(centre.cy, stage[1] - centre.cy) - halfH - ORBIT_EDGE_CLEARANCE) /
        Math.hypot(finalA * Math.sin(i), finalB * Math.cos(i)),
    ),
    0,
    1,
  );
  const point = satellitePointAt(
    mix(opening[0], bound * finalA, ease),
    mix(opening[1], bound * finalB, ease),
    inclinationDegrees,
    angleDegrees,
  );
  return {
    x: centre.cx + point.x,
    y: centre.cy + point.y,
    width: art[0] * mix(1, FINALE_ART_SCALE, ease),
    height: art[1] * mix(1, FINALE_ART_SCALE, ease),
  };
};

/**
 * Whether the moon's figure is close enough to the Earth's limb to need its ring:
 * centre distance against the Earth's radius plus the lunar diameter — i.e. the
 * ring lights while the gap between the two limbs is under one lunar radius, the
 * pass in which the disc rides the planet's edge and the two slates read as one
 * shape. No bounding-box or circle-intersection machinery, because the only
 * consumer is one CSS state.
 *
 * Measured, not guessed: at the reference 1568-wide viewport — the Earth 437.3px
 * across, 0.52 of the 841px stage height — the lunar centre passes 251.5px from the
 * Earth's centre against a limb-against-limb 244.9px, so the discs never quite
 * touch: 6.6px of sky at the tightest, with the caption over the planet's surface
 * across the whole approach. A pure contact test would therefore leave the ring
 * unlit for the entire finale, and a permanent one would glow in open space; this
 * holds it for the ~2min of the orbit around that pass.
 */
const moonOverEarth = (
  box: Placement,
  centre: { cx: number; cy: number },
  earthDiameter: number,
): boolean => Math.hypot(box.x - centre.cx, box.y - centre.cy) < earthDiameter / 2 + box.width;

/**
 * Every finale body's inline box for one frame: the moon's scroll-led entrance
 * along its orbit, and both satellites. The entrance slides the lunar centre from
 * entirely off the right edge at entry 0 — whatever the orbital phase — to its
 * orbital point at entry 1, so scroll alone owns how far the moon has come in and
 * retreating retraces exactly that line. Satellites start at 90° and advance 2°
 * per lunar degree: with the shared 0.5°/s clock that is 1°/s, a circuit every six
 * minutes rather than the old one-minute lap that read as busy next to the text.
 * Sputnik carries `SPUTNIK_PHASE_OFFSET` on top of that shared phase, so the two
 * never travel in lockstep. They stay upright, because no rotation is applied to
 * their artwork, and their boxes are absolute stage pixels.
 *
 * `overEarth` is true while the moon's disc is inside one lunar radius of the
 * Earth's limb — the pass in which the two slates meet across a few pixels of sky
 * and the caption crosses the planet — and is what the limb ring in the page's CSS
 * is keyed on: the disc is painted the same slate the Earth's land is, so there is
 * nothing else to read it by. It is computed from the mixed box below, not the
 * pure orbit, so it describes what is actually painted.
 */
export const bodyLayout = (
  width: number,
  height: number,
  earthDiameter: number,
  closingProgress: number,
  spinDegrees: number,
  ringOne: [number, number],
  ringThree: [number, number],
): {
  moon: Placement;
  iss: Placement;
  sputnik: Placement;
  overEarth: boolean;
} => {
  const centre = globeCentre(width, height);
  const moon = moonOrbitAt(width, height, earthDiameter, spinDegrees);
  const entry = clamp((closingProgress - 0.35) / 0.65, 0, 1);
  const angle = 90 + 2 * spinDegrees;
  const ease = smoothstep(clamp(closingProgress, 0, 1));
  const stage: [number, number] = [width, height];
  const phone = width < 768;
  const moonBox: Placement = {
    x: (1 - entry) * (width + moon.diameter / 2 + 12) + entry * moon.x,
    y: moon.y,
    width: moon.diameter,
    height: moon.diameter,
  };
  return {
    moon: moonBox,
    overEarth: moonOverEarth(moonBox, centre, earthDiameter),
    iss: satelliteBox(
      centre,
      stage,
      ease,
      angle,
      ringOne,
      ISS_INCLINATION,
      earthDiameter,
      phone ? ISS_ART_PHONE : ISS_ART,
    ),
    sputnik: satelliteBox(
      centre,
      stage,
      ease,
      angle + SPUTNIK_PHASE_OFFSET,
      ringThree,
      SPUTNIK_INCLINATION,
      earthDiameter,
      phone ? SPUTNIK_ART_PHONE : SPUTNIK_ART,
    ),
  };
};

/*
 * Pure statements the controller needs and has no room for: which of the
 * payload's cities the camera may frame and where that cluster sits, the label
 * geometry it hands the map for them, and the shape check for the vendored
 * module it loads by path.
 */

/** A city whose dataset coordinates resolved — cities without coords cannot be mapped. */
export type LocatedCity = LandingCity & { lat: number; lng: number };

/** Valid dataset coordinates only: a NaN or out-of-range pair parks the camera in the void. */
export const isLocated = (city: LandingCity): city is LocatedCity =>
  typeof city.lat === "number" &&
  typeof city.lng === "number" &&
  Number.isFinite(city.lat) &&
  Number.isFinite(city.lng) &&
  Math.abs(city.lat) <= 90 &&
  Math.abs(city.lng) <= 180;

/** The frame a payload with no mappable city opens on. */
export const WORLD_CENTER: [number, number] = [20, 20];

/**
 * Where the displayed city set sits on the globe, so the opening camera is aimed
 * at the payload the page actually rendered rather than at a fixed corner of the
 * world. It reads the payload rather than a viewport query, so its answer cannot
 * change as the globe rotates; the unit-vector mean is what lands [0,179] with
 * [0,-179] on the antimeridian instead of dragging the pair across Greenwich; and
 * it weights cities equally, because an event count would let one busy city own the
 * frame. One city is its own centre, and an empty payload falls back to the world.
 */
export const cityClusterCenter = (cities: readonly LocatedCity[]): [number, number] => {
  if (cities.length === 0) return WORLD_CENTER;
  let x = 0;
  let y = 0;
  let z = 0;
  for (const city of cities) {
    const lat = (city.lat * Math.PI) / 180;
    const lng = (city.lng * Math.PI) / 180;
    x += Math.cos(lat) * Math.cos(lng);
    y += Math.cos(lat) * Math.sin(lng);
    z += Math.sin(lat);
  }
  // Antipodal cities cancel to no mean at all: the first city is a finite, stable
  // answer where a direction picked out of that wreckage would be noise.
  const length = Math.hypot(x, y, z);
  if (length < 1e-6) return [cities[0].lat, cities[0].lng];
  return [
    (Math.asin(z / length) * 180) / Math.PI,
    (Math.atan2(y / length, x / length) * 180) / Math.PI,
  ];
};

/**
 * Where the opening frame is actually aimed. Desktop is the cluster itself; a
 * phone nudges it east by the gap between the two offsets, because `cameraAt`
 * subtracts the desktop OPENING_LNG_OFFSET from whatever it is handed — so the
 * phone branch is exactly the phone opening offset, and only the phone branch,
 * that the narrower stage needs to keep its far city in frame.
 */
export const openingFocus = (cities: readonly LocatedCity[], width: number): [number, number] => {
  const cluster = cityClusterCenter(cities);
  return width < 768
    ? [cluster[0], cluster[1] + OPENING_LNG_OFFSET - OPENING_LNG_OFFSET_PHONE]
    : cluster;
};

/** GeoJSON payload for the orange city labels. */
export type CityFeature = {
  type: "Feature";
  properties: { name: string };
  geometry: { type: "Point"; coordinates: [number, number] };
};

/**
 * One city's label feature at a given position. The text is upper-cased to plain
 * ASCII because the vendored glyph set (Open Sans Bold) has no İ/ı/ş coverage;
 * building it in one place is what keeps the behind-planet copy of a name
 * identical to the facing one — only its coordinates differ.
 */
export const labelFeature = (city: LocatedCity, coordinates: [number, number]): CityFeature => ({
  type: "Feature",
  properties: { name: toAsciiUpper(city.name) },
  geometry: { type: "Point", coordinates },
});

/** The label features for the cities the camera can target. */
export const cityFeatures = (cities: LocatedCity[]): CityFeature[] =>
  cities.map((city) => labelFeature(city, [city.lng, city.lat]));

/**
 * The vendored MapLibre namespace, as a type only: MapLibre is served as
 * pre-built ESM from public/vendor (its worker bootstrap does not survive
 * bundling) and imported at runtime by the controller, so nothing here reaches
 * the bundle graph.
 */
export type ML = typeof MaplibreGl;

/**
 * The vendored ESM arrives through a runtime import, so nothing guarantees its
 * module shape: check the two constructors the map uses before touching it.
 */
export const isMaplibreModule = (value: unknown): value is ML =>
  typeof value === "object" &&
  value !== null &&
  "Map" in value &&
  "Marker" in value &&
  typeof value.Map === "function" &&
  typeof value.Marker === "function";
